import React, {createRef, useState, useContext, useRef, useEffect} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import moment from "moment";
import __ from "lodash";
import MaterialTable from "@material-table/core";
import { TablePagination, Checkbox, Select, MenuItem, useTheme } from "@material-ui/core";
import SystemContext from "../../../SystemContext";
import { ValidatorColumn, ColumnWidth } from "../../../blocks/ColumnHelper";
import CostingsValidator from "../validators/CostingsValidator";
import CurrencyLookup from "../../Currencies/components/CurrencyLookup";

import { useCostings, useAddCosting, useUpdateCosting, useDeleteCosting } from "../../../services/costings";
import { more as moreCostings} from "../../../services/costings/useCostings";

import { 
  MaskedText, 
  MaskedNumber, 
  DatePicker,
} from "../../../controls";
import { defaultComponents, defaultComponentsScroll, MaterialTableBody } from '../../../controls/Material-Table-Components';
import useDimensions from '../../../blocks/useDimensions';
import ViewCostingDialog from './ViewCostingDialog';
import AddCostingDialog from './AddCostingDialog';
import EditCostingDialog from './EditCostingDialog';
import { getApiSitePrefix } from '../../../functions/SiteDefaults';
import { getCostingDefaultValues } from '../includes/costingDialogFunctions';

const apiSitePrefix = getApiSitePrefix();

const dateFormat = "DD/MM/YYYY";
const today = moment().toISOString();

const CostingList = (_props) => {
  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const validatorColumn = ValidatorColumn(CostingsValidator, true);

  const props = {
    ..._props
  }
  const [state, setState] = useState({
    canEdit: props.canEdit,
    activeRow: null, 
    scrollTop: undefined,
    noMoreRows: false,
  });
  const [defaultValues, setDefaultValues] = useState(null);

  const classes = props.classes;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const eventBus = SystemData.eventBus;
  useEffect(()=> {
    const handleAddCosting = ()=> {
      eventBus.trigger('AddCostingDialog.open');
    };
    const handleEditCosting = (record)=> {
      eventBus.trigger('EditCostingDialog.open', record);
    };
    const handleExportCosting = (record) => {

    }
    eventBus.bind('CostingList.Add', handleAddCosting);
    eventBus.bind('CostingList.Edit', handleEditCosting);
    eventBus.bind('CostingList.Excel', handleExportCosting);
    return () => {
      eventBus.unbind('CostingList.Add', handleAddCosting);
      eventBus.unbind('CostingList.Edit', handleEditCosting);
      eventBus.unbind('CostingList.Excel', handleExportCosting);
    }
  }, [eventBus]);


  let selectedRow = -1;
  const handleRowClick = (event, rowData) => {
    if (rowData.tableData.id >= 0) {
      selectedRow = rowData.tableData.id;
    }
  }

  const handleAddCosting = useAddCosting();
  const handleUpdateCosting = useUpdateCosting();
  const handleDeleteCosting = useDeleteCosting();

  const queryClient = useQueryClient();
  const extraKeys = [];

  const loadedCostings = () => {
    console.debug('costings loaded');
    if (state.scrollTop !== undefined) {
      setState({...state, scrollTop: undefined});
      noMoreRows.current=false;
    }
  }

  let noMoreRows = useRef(false);

  const onScrollAtEnd = (params) => {
    if (!noMoreRows.current) {
      console.log(`getting more costings after ${params.dataLength}`);
      setState({...state, scrollTop: params.scrollTop});
      moreCostings({tableRef, queryClient, extraKeys, params: props.searchCriteria, rowCount: 200, callback: (rows, extraKeys, addedCount)=> {
        console.debug('extra costings loaded');
        if (addedCount < 200) {
          noMoreRows.current=true;
        }
      }});
    } else {
      console.log(`No more rows`);
    }
  }

  const costings = useCostings({tableRef, queryClient, extraKeys, params: props.searchCriteria, rowCount: 500, callback: loadedCostings});

  if (!defaultValues || __.isEmpty(defaultValues))
    getCostingDefaultValues(apiSitePrefix, SystemData, setDefaultValues);

  const getColumns = (props) => { 
    const baseColumn = {
      cellStyle: { fontSize:14, padding: "8px 2px" },
      headerStyle: { padding: "8px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
    };
    const formatChars={ 
      "U": "[A-Z]", 
      "A": "[A-Z0-9]", 
      "a": "[A-Za-z0-9]",
      "i": "[A-Za-z0-9\\-]",
      "9": "[0-9]",
      "D": "[0-9.]",
      "-": "-",
      "x": "."
    }
    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      {...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, undefined, undefined, undefined, false)),
        title: "Costing Number",
        field: "costingNumber",
        render: (rowData) => (
          <MaskedNumber
            displayType="text"
            readOnly={true}
            format="UCN ######"
            value={rowData.costingNumber}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, undefined, undefined, undefined, false)),
        title: "Costing Date",
        field: "costingDate",
        type: "date",
        emptyValue: today,
        initialEditValue:today,
        editComponent: (props) => (
            <DatePicker
              // minDate={today}
              value={props.value}
              disableToolbar={true}
              autoOk={true}
              format={dateFormat}
              onChange={props.onChange}
            />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(200, undefined, undefined, undefined, undefined, undefined, false)),
        title: "Route",
        field: "route",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(300, undefined, undefined, undefined, undefined, undefined, false)),
        title: "Trip Routing",
        field: "tripRouting",
        initialEditValue: "",
        editComponent: (props) => (
          <MaskedText
            mask={"x".repeat(100)}
            maskChar={""}
            alwaysShowMask={false}
            formatChars={formatChars}
            value={props.value}
            onChange={props.onChange}
            inputProps={defaultEditProps}
          />
        )
      },
      {
        ...baseColumn,
        render: (()=> (<>&nbsp;</>))
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, undefined, undefined, undefined, false)),
        title: "Revenue ($)",
        field: "totalRevenue",
        type: "numeric",
        render: (props) => (
          <MaskedNumber
            displayType="text"
            value={props.totalRevenue}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, undefined, undefined, undefined, false)),
        title: "Total Cost ($)",
        field: "requiredRevenue",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        render: (rowData) => (
          <MaskedNumber
            displayType="text"
            value={rowData.requiredRevenue}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, undefined, undefined, undefined, false)),
        title: "Profit ($)",
        field: "profit",
        type: "numeric",
        emptyValue: null,
        initialEditValue: null,  
        render: (rowData) => (
          <MaskedNumber
            displayType="text"
            value={rowData.profit}
            decimalScale={2}
            fixedDecimalScale={true}
          />
        )
      },
      {
        ...baseColumn,
        ...(ColumnWidth(20, 0, 20, {maxWidth: 20}, {maxWidth: 20}, {maxWidth: 20}, false)),
        render: (()=> (<>&nbsp;</>))
      }
    ];
  };
  
  const [tableContainerRef, {height: tableHeight}] = useDimensions({});
  const theader = tableContainerRef.current?.querySelector('thead');
  const thead = theader ? theader.clientHeight:40;
  const bodyHeight = tableHeight - (thead -8);

  return (isAuthenticated &&
    <>
      <div ref={tableContainerRef} className={state.canEdit?classes.costingsListEdit:classes.costingsList} style={{width: "100%", minWidth: "100%", height: `calc(100vh - ${props.height + 60}px)`}}>
        <MaterialTable
            tableRef={tableRef}
            style={{
              border: "none",
              boxShadow: "none",
              borderRadius: 0,
            }}
            onRowClick={handleRowClick}
            options={{
              tableRef,
              actionsColumnIndex: -1,
              highlightRowStyle: {backgroundColor: "#F69259"},
              cellStyle: {
                padding: "8px 2px",
              },
              headerRowStyle: {
                display: "table",
                width: "100%",
              },
              headerStyle: {
                backgroundColor: theme.palette.tableHeader.backgroundColor,
                color: theme.palette.tableHeader.textColor,
                padding: 2,
              },
              draggable: false,
              toolbar: false,
              toolbarButtonAlignment: "right",
              detailPanelType: "single",
              padding: 'dense',
              paging: false,
              filtering: false,
              searchFieldAlignment: "right",
              addRowPosition: "first",
              onScrollAtEnd: onScrollAtEnd,
              scrollTop: state.scrollTop
            }}
            components={{
              ...defaultComponents,
              Body: (props) => (
                <MaterialTableBody
                  {...props}
                  rowHeight={40}
                  overscanRowCount={10}
                  addVirtualScrolling={true}
                  bodyHeight={bodyHeight}
                  headerHeight={thead}
                  filterRowHeight={46}
                />
              ),
            }}
            // components={{
            //   Pagination: (props) => (
            //       <TablePagination
            //           {...props}
            //           rowsPerPageOptions={[5, 10, 15]}
            //       />
            //   ),
            // }}
            actions={[
              (rowData) => {
                return (
                {
                  hidden: false,
                  icon: "preview",
                  iconProps: { style: {color: (rowData.tableData.id === selectedRow ? '#ffffff' : theme.palette.primary.main)}},
                  tooltip: 'View Costing',
                  isFreeAction: false,
                  onClick: (e, rowData) => {
                    eventBus.trigger("ViewCostingDialog.open", rowData);
                  }
                }
                )
              },
              // (rowData) => {
              //   return (
              //   {
              //     hidden: false,
              //     icon: "save_alt",
              //     iconProps: { style: {color: (rowData.tableData.id === selectedRow ? '#ffffff' : theme.palette.primary.main)}},
              //     tooltip: 'Download Excel',
              //     isFreeAction: false,
              //     onClick: (e, rowData) => {
              //       eventBus.trigger("CostingList.Excel", rowData);
              //     }
              //   }
              //   )
              // },
              (rowData) => {
                return (
                {
                  hidden: false,
                  icon: "edit",
                  iconProps: { style: {color: (rowData.tableData.id === selectedRow ? 'primary)' : 'primary')}},
                  tooltip: 'Edit',
                  isFreeAction: false,
                  onClick: (e, rowData) => {
                    eventBus.trigger("EditCostingDialog.open", rowData);
                  }
                }
                )
              },
            ]}
            title="Costing Management"
            columns={getColumns({canEdit: state.canEdit})}
            data={costings.data}
            editable={{
              isEditable: () => {
                return false; //state.canEdit;
              },
              isDeletable: () => {
                return state.canEdit;
              },
            }}
        />
      </div>   
      
      <ViewCostingDialog
        SystemData={SystemData}
        extraKeys={extraKeys}
        apiSitePrefix={apiSitePrefix}
        defaultValues={defaultValues}
        classes={classes}
      />
      <AddCostingDialog
        SystemData={SystemData}
        extraKeys={extraKeys}
        apiSitePrefix={apiSitePrefix}
        defaultValues={defaultValues}
        classes={classes}
        handleAdd={handleAddCosting}
        afterSave={()=>{
          const { key } = ["Costings"];
          queryClient.invalidateQueries(key);
          eventBus.trigger("CostingList.refresh");
        }}
      />
      <EditCostingDialog
        SystemData={SystemData}
        extraKeys={extraKeys}
        apiSitePrefix={apiSitePrefix}
        defaultValues={defaultValues}
        classes={classes}
        handleUpdate={handleUpdateCosting}
        handleAdd={handleAddCosting}
        afterAdd={(costingRecord)=>{
          if (costingRecord) {
            SystemData.alert("Info", `Costing Saved as UCN-${costingRecord?.costingNumber}`);
            const { key } = ["Costings"];
            queryClient.invalidateQueries(key);
            eventBus.trigger("CostingList.refresh");
          } else {
            SystemData.alert("Error", `A problem occurred while saving the Costing.`);
          }
        }}
      />
    </> 
  );

}

export default CostingList;
